import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import './App.css';
import './styles/appServiceCatalog.css';
import './styles/processPage.css';
import './styles/siteFooter.css';
import Hero from './components/Hero';
import LowerSections, { FinalSection } from './components/LowerSections';
import MouseTrail from './components/MouseTrail';
import PageLoader from './components/PageLoader';
import Navbar from './components/Navbar';
import RegionRail from './components/RegionRail';
import { serviceRouteComponents } from './routes/serviceRoutes';
import { caseStudyRouteComponents } from './routes/caseStudyRoutes';
import { quickPagesData } from './data/quickPagesData';
import { appServicesCatalog } from './data/appServicesCatalog';

const AboutUsSection = lazy(() => import('./components/AboutUsSection'));
const SiteFooter = lazy(() => import('./components/Shared/SiteFooter'));
const CaseStudiesPage = lazy(() => import('./components/CaseStudiesPage'));
const QuickAccessPage = lazy(() => import('./pages/QuickAccessPage'));
const PpcMobileAppLanding = lazy(() => import('./pages/PpcMobileAppLanding'));
const AppServiceLanding = lazy(() => import('./pages/AppServiceLanding'));
const ConnectPage = lazy(() => import('./pages/ConnectPage'));
const ProcessPage = lazy(() => import('./pages/ProcessPage'));
const ThankYouPage = lazy(() => import('./pages/ThankYouPage'));
const routeLoaderImage = `${process.env.PUBLIC_URL}/images/appivorous-mark-light.svg`;
const ppcLandingPaths = ['/us', '/us/'];
const legacyPpcLandingPaths = ['/get-free-consultancy-mobile-app-development-services', '/get-free-consultancy-mobile-app-development-services/'];

function RouteTransition({ children }) {
  return (
    <motion.div
      className="route-transition"
      initial={{ opacity: 0, y: 26 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -18 }}
      transition={{ duration: 0.46, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

function RouteFallback() {
  return (
    <div className="route-fallback" aria-live="polite">
      <span />
    </div>
  );
}

function PageChangeLoader({ active }) {
  return (
    <AnimatePresence>
      {active && (
        <motion.div
          className="page-change-loader"
          role="status"
          aria-live="polite"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.34, ease: 'easeOut' }}
        >
          <motion.div
            className="page-loader-mark page-change-mark"
            initial={{ scale: 0.84, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.94, opacity: 0 }}
            transition={{ duration: 0.34, ease: [0.22, 1, 0.36, 1] }}
          >
            <img src={routeLoaderImage} alt="Appivorous loading" />
          </motion.div>
          <span className="loader-sr-text">Loading page</span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function HomePage() {
  useEffect(() => {
    document.title = 'Appivorous | Digital Design and Development Agency';
  }, []);

  return (
    <>
      <Hero />
      <LowerSections />
      <FinalSection />
      <SiteFooter />
    </>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  const [pageChanging, setPageChanging] = useState(false);
  const previousPath = useRef(location.pathname);
  const path = location.pathname.replace(/\/$/, '') || '/';
  const isPpcLanding = [...ppcLandingPaths, ...legacyPpcLandingPaths].includes(location.pathname)
    || [...ppcLandingPaths, ...legacyPpcLandingPaths].includes(path);
  const isExactMirror = path === '/connect' || path === '/connect-with-us' || appServicesCatalog.some((service) => service.path === path);

  useEffect(() => {
    if (previousPath.current === location.pathname) return undefined;

    previousPath.current = location.pathname;
    setPageChanging(true);

    const timer = window.setTimeout(() => setPageChanging(false), 1180);
    return () => window.clearTimeout(timer);
  }, [location.pathname]);

  useEffect(() => {
    const finishMirrorLoad = () => setPageChanging(false);
    window.addEventListener('appivorous:mirror-ready', finishMirrorLoad);
    return () => window.removeEventListener('appivorous:mirror-ready', finishMirrorLoad);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  return (
    <main className={`site-shell ${isExactMirror ? 'mirror-route' : ''}`}>
      {!isPpcLanding && <MouseTrail />}
      {!isPpcLanding && <Navbar />}
      {!isPpcLanding && !isExactMirror && <RegionRail />}
      <PageChangeLoader active={pageChanging} />
      <Suspense fallback={<RouteFallback />}>
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<RouteTransition><HomePage /></RouteTransition>} />
            <Route path="/about-us" element={<RouteTransition><AboutUsSection /></RouteTransition>} />
            <Route path="/about_us.php" element={<RouteTransition><AboutUsSection /></RouteTransition>} />
            <Route path="/our-process" element={<RouteTransition><ProcessPage /></RouteTransition>} />
            <Route path="/thankyou" element={<RouteTransition><ThankYouPage /></RouteTransition>} />
            <Route path="/case-study" element={<RouteTransition><CaseStudiesPage /></RouteTransition>} />
            <Route path="/case-study.php" element={<RouteTransition><CaseStudiesPage /></RouteTransition>} />
            <Route path="/connect" element={<RouteTransition><ConnectPage /></RouteTransition>} />
            <Route path="/connect-with-us" element={<RouteTransition><ConnectPage /></RouteTransition>} />
            <Route path="/services" element={<RouteTransition><AppServiceLanding /></RouteTransition>} />
            {appServicesCatalog.map((service) => (
              <Route key={service.path} path={service.path} element={<RouteTransition><AppServiceLanding service={service} /></RouteTransition>} />
            ))}
            {ppcLandingPaths.map((ppcPath) => (
              <Route key={ppcPath} path={ppcPath} element={<RouteTransition><PpcMobileAppLanding /></RouteTransition>} />
            ))}
            {legacyPpcLandingPaths.map((legacyPath) => (
              <Route key={legacyPath} path={legacyPath} element={<Navigate to="/us" replace />} />
            ))}
            {serviceRouteComponents.flatMap(({ path: servicePath, legacyPath, Component }) => [
              <Route key={servicePath} path={servicePath} element={<RouteTransition><Component /></RouteTransition>} />,
              <Route key={legacyPath} path={legacyPath} element={<RouteTransition><Component /></RouteTransition>} />,
            ])}
            {caseStudyRouteComponents.flatMap(({ path: casePath, legacyPath, Component }) => [
              <Route key={casePath} path={casePath} element={<RouteTransition><Component /></RouteTransition>} />,
              <Route key={legacyPath} path={legacyPath} element={<RouteTransition><Component /></RouteTransition>} />,
            ])}
            {quickPagesData.flatMap(({ path: quickPath, legacyPath, slug }) => [
              <Route key={quickPath} path={quickPath} element={<RouteTransition><QuickAccessPage slug={slug} /></RouteTransition>} />,
              <Route key={legacyPath} path={legacyPath} element={<RouteTransition><QuickAccessPage slug={slug} /></RouteTransition>} />,
            ])}
            <Route path="*" element={<RouteTransition><HomePage /></RouteTransition>} />
          </Routes>
        </AnimatePresence>
      </Suspense>
    </main>
  );
}

function App() {
  useEffect(() => {
    let isSubscribed = false;
    let subscriptionTimer;
    const maximizeTimers = new Set();

    const maximizeChat = () => {
      window.LiveChatWidget?.call?.('maximize');
    };

    const scheduleMaximize = (delay) => {
      const timer = window.setTimeout(() => {
        maximizeTimers.delete(timer);
        maximizeChat();
      }, delay);

      maximizeTimers.add(timer);
    };

    const onNewEvent = (event) => {
      const isIncomingMessage = ['message', 'rich_message'].includes(event?.type)
        && event?.payload?.author?.type !== 'customer';

      if (!isIncomingMessage) return;

      // Let LiveChat finish updating its minimized state before reopening it.
      maximizeChat();
      scheduleMaximize(250);
    };

    const subscribe = () => {
      const liveChatWidget = window.LiveChatWidget;

      if (isSubscribed || typeof liveChatWidget?.on !== 'function') return;

      liveChatWidget.on('new_event', onNewEvent);
      isSubscribed = true;

      if (subscriptionTimer) {
        window.clearInterval(subscriptionTimer);
        subscriptionTimer = undefined;
      }
    };

    subscribe();
    if (!isSubscribed) subscriptionTimer = window.setInterval(subscribe, 100);

    return () => {
      if (subscriptionTimer) window.clearInterval(subscriptionTimer);
      maximizeTimers.forEach((timer) => window.clearTimeout(timer));

      const liveChatWidget = window.LiveChatWidget;
      if (isSubscribed && typeof liveChatWidget?.off === 'function') {
        liveChatWidget.off('new_event', onNewEvent);
      }
    };
  }, []);

  return (
    <BrowserRouter>
      <PageLoader />
      <AnimatedRoutes />
    </BrowserRouter>
  );
}

export default App;
